<template>
  <div class="bg-white shadow-md rounded-xl p-4 m-4">
    <MonkeyLoader v-if="isLoading" />
    <h2 class="text-xl font-semibold mb-4 text-gray-800">
      SPK Subcon Update
    </h2>
    <form @submit.prevent="saveForm">
      <div class="grid grid-cols-1 gap-4">
        <div class="form-section">
          <div class="mb-4 flex gap-4">
            <div class="w-5/6">
              <label class="block text-gray-700 text-sm font-bold mb-2" for="spkNumber">
                SPK #:
              </label>
              <select id="spkNumber" v-model="formData.spkNum"
                class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required>
                <option value="" disabled>-- Pilih SPK # --</option>
                <option v-for="spk in spkListOptions" :key="spk.id" :value="spk.id">
                  {{ spk.spkNum }}
                </option>
              </select>
            </div>
            <div class="w-1/6">
              <label class="block text-gray-700 text-sm font-bold mb-2" for="spkDate">
                SPK Date:
              </label>
              <input type="date" id="spkDate" v-model="formData.spkDate"
                class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" />
            </div>
          </div>
          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="subcon">
              Sub Con:
            </label>
            <select
              class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              id="subcon" v-model="formData.subcon" required>
              <option value="">-- Pilih Subcon --</option>
              <option v-for="subconOption in subconOptions" :key="subconOption.id" :value="subconOption.id">
                {{ subconOption.entityName }}
              </option>
            </select>
          </div>

          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="subconContactName">
              Contact Name:
            </label>
            <select
              class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              id="subconContactName" v-model="formData.subconContactName" @change="updateSubconInfo" required>
              <option value="">-- Pilih Contact --</option>
              <option v-for="contactOption in contactOptions" :key="contactOption.id" :value="String(contactOption.id)">
                {{ contactOption.contactName }}</option>
            </select>
          </div>

          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="subconJabatan">
              Job Title:
            </label>
            <input
              class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              type="text" id="subconJabatan" v-model="subconInfo.jabatan" disabled />
          </div>

          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="subconHp">
              No.Hp:
            </label>
            <input
              class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              type="text" id="subconHp" v-model="subconInfo.noHp" disabled />
          </div>

          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="pekerjaan">
              Job:
            </label>
            <div class="flex items-center gap-2">
              <select class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
                id="pekerjaan" v-model="formData.pekerjaan" required>
                <option value="">-- Pilih Pekerjaan --</option>
                <option v-for="job in jobOptions" :key="job.id" :value="job.id">{{ job.jobname }}</option>
              </select>
              <button type="button" @click="openJobModal"
                class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
                +
              </button>
            </div>
          </div>
          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="remark">
              Remark:
            </label>
            <textarea
              class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              id="remark" rows="3" v-model="formData.remark" required></textarea>
          </div>

          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="poNumber">
              PO #:
            </label>
            <v-select id="poNumber" v-model="selectedPoObject" :options="poOptions" label="poNumber"
              :get-option-label="poOptionLabel" placeholder="-- Pilih PO # --" class="input-border"
              :class="{ 'border-red-500': !selectedPoObject && formData.poNumber === '' }">
              <template #option="option">
                <div>{{ option.poNumber }} ({{ option.siteNames }})</div>
              </template>
              <template #selected-option="option">
                <div>{{ option.poNumber }} ({{ option.siteNames }})</div>
              </template>
            </v-select>
          </div>

          <div class="mb-4 flex items-center">
            <div class="w-5/6">
              <label class="block text-gray-700 text-sm font-bold mb-2" for="siteName">
                Site Name:
              </label>
              <select
                class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                id="siteName" v-model="formData.siteName">
                <option value="">-- Pilih Site --</option>
                <option v-for="site in siteOptions" :key="site.id" :value="site.id">
                  {{ site.name }}
                </option>
              </select>
            </div>
            <div class="w-1/6 pl-4 flex justify-end">
              <button type="button" @click="addRowToDetailPekerjaan"
                class="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline">
                Add To List
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="data-grid-container mt-6">
        <div class="overflow-x-auto">
          <table class="min-w-full table-auto">
            <thead>
              <tr>
                <th class="px-4 py-2 border-b text-left">Dlt.</th>
                <th class="px-4 py-2 border-b text-left">No</th>
                <th class="px-4 py-2 border-b text-left">PO Number</th>
                <th class="px-4 py-2 border-b text-left">Sitename</th>
                <th class="px-4 py-2 border-b text-left">Area</th>
                <th class="px-4 py-2 border-b text-left">Project Name</th>
                <th class="px-4 py-2 border-b text-left">Price</th>
                <th class="px-4 py-2 border-b text-left">Start Date</th>
                <th class="px-4 py-2 border-b text-left">End Date</th>
                <th class="px-4 py-2 border-b text-left sr-only">Site ID</th>
                <th class="px-4 py-2 border-b text-left sr-only">POID</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(detail, index) in detailPekerjaan" :key="index">
                <td class="px-4 py-2 border-b text-center">
                  <input type="checkbox" v-model="detail.checked" />
                </td>
                <td class="px-4 py-2 border-b">{{ index + 1 }}</td>
                <td class="px-4 py-2 border-b">
                  <input type="text" v-model="detail.poNumber"
                    class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700" readonly>
                </td>
                <td class="px-4 py-2 border-b">
                  <input type="text" v-model="detail.sitename"
                    class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline">
                </td>
                <td class="px-4 py-2 border-b"><input type="text" v-model="detail.area"
                    class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    readonly></td>
                <td class="px-4 py-2 border-b"><input type="text" v-model="detail.projectName"
                    class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    readonly></td>
                <td class="px-4 py-2 border-b">
                  <input type="text" v-model="detail.priceFormatted" @input="updatePrice(detail)"
                    @blur="formatPriceOnBlur(detail)"
                    class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline">
                </td>
                <td class="px-4 py-2 border-b"><input type="date" v-model="detail.startDate"
                    class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline">
                </td>
                <td class="px-4 py-2 border-b"><input type="date" v-model="detail.endDate"
                    class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline">
                </td>
                <td class="px-4 py-2 border-b sr-only">{{ detail.Id }}</td>
                <td class="px-4 py-2 border-b sr-only">{{ detail.poId }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="gspe-info mt-6 border rounded p-4">
        <h3 class="text-lg font-semibold mb-2 text-gray-800">GSPE</h3>
        <div class="mb-4">
          <label class="block text-gray-700 text-sm font-bold mb-2" for="gspeNama">
            Name:
          </label>
          <select
            class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            id="gspeNama" v-model="formData.gspeNama" required>
            <option value="">-- Pilih Nama --</option>
            <option v-for="user in gspeNameOptions" :key="user.id" :value="user.id">
              {{ user.username }}
            </option>
          </select>
        </div>
        <div class="mb-4">
          <label class="block text-gray-700 text-sm font-bold mb-2" for="gspeJabatan">
            Job Title:
          </label>
          <input
            class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            type="text" id="gspeJabatan" v-model="formData.gspeJabatan" />
        </div>

      </div>
      <div class="flex justify-center mt-4 gap-2">
        <button type="submit"
          class="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-6 rounded focus:outline-none focus:shadow-outline w-1/6">
          Update
        </button>
        <button type="button" @click="exitForm"
          class="bg-gray-300 hover:bg-gray-400 text-gray-700 font-bold py-2 px-6 rounded focus:outline-none focus:shadow-outline w-1/6">
          Exit
        </button>
      </div>
    </form>
    <div v-if="isJobModalVisible" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white p-6 rounded-lg shadow-xl w-1/3">
        <h3 class="text-lg font-bold mb-4">Add New Job</h3>
        <div class="mb-4">
          <label for="newJobName" class="block text-sm font-medium text-gray-700">Job Name:</label>
          <input type="text" id="newJobName" v-model="newJobName"
            class="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="e.g., Installasi FO" @keyup.enter="handleSaveNewJob" required>
        </div>
        <div class="flex justify-end gap-2">
          <button @click="closeJobModal" type="button"
            class="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded">
            Cancel
          </button>
          <button @click="handleSaveNewJob" type="button" :disabled="isSavingJob"
            class="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
            :class="{ 'opacity-50 cursor-not-allowed': isSavingJob }">
            <span v-if="isSavingJob">Saving...</span>
            <span v-else>Save</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import MonkeyLoader from '../Layout/MonkeyLoader.vue';
export default {
  components: {
    'v-select': vSelect, MonkeyLoader
  },
  data() {
    return {
      // --- FORM DATA ---
      formData: {
        spkNum: '',
        spkDate: this.getCurrentDate(),
        subcon: '',
        subconContactName: '',
        pekerjaan: '',
        remark: '',
        poNumber: '',
        siteName: '',
        gspeNama: '',
        gspeJabatan: '',
      },
      subconInfo: {
        jabatan: '',
        noHp: '',
      },
      detailPekerjaan: [],
      selectedProjectData: null,
      selectedPoObject: null,
      isLoading: false, // PENAMBAHAN BARU

      // --- DROPDOWN OPTIONS ---
      spkListOptions: [],
      subconOptions: [],
      contactOptions: [],
      poOptions: [],
      siteOptions: [],
      gspeNameOptions: [],
      jobOptions: [], // PENAMBAHAN BARU

      // --- MODAL & STATE MANAGEMENT --- // PENAMBAHAN BARU
      isJobModalVisible: false, // PENAMBAHAN BARU
      newJobName: '', // PENAMBAHAN BARU
      isSavingJob: false, // PENAMBAHAN BARU
    };
  },

  methods: {
    // =================================================================
    // SECTION: DATA LOADING & INITIALIZATION
    // =================================================================
    async loadSpkListOptions() {
      try {
        const response = await axios.get(`${window.location.origin}/api/spksubconfiles/authorization`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        this.spkListOptions = response.data;
      } catch (error) {
        console.error('Gagal memuat SPK list:', error);
        this.spkListOptions = [];
      }
    },

    loadSubconOptions() {
      axios.get(`${window.location.origin}/api/entities/type4`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
        .then(response => {
          this.subconOptions = response.data;
        })
        .catch(error => {
          console.error('Error fetching subcon options:', error);
          this.subconOptions = [];
        });
    },

    async loadContactOptions(entityId) {
      if (!entityId) {
        this.contactOptions = [];
        return;
      }
      try {
        const response = await axios.get(`${window.location.origin}/api/contacts/${entityId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        this.contactOptions = response.data;
      } catch (error) {
        console.error("Error fetching contact options:", error);
        this.contactOptions = [];
      }
    },

    async loadPoOptions() {
      try {
        const response = await axios.get('https://crm-local.iotech.my.id/po/subcon?service=true&bpo=true', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const poData = response.data.map(po => ({
          id: po.id,
          poNumber: po.poNumber,
          sites: po.sites,
          siteNames: po.sites.map(site => site.name).join(', '),
        })).sort((a, b) => a.poNumber.localeCompare(b.poNumber));
        this.poOptions = poData;
      } catch (error) {
        console.error('Error fetching PO options:', error);
        this.poOptions = [];
      }
    },

    async loadGspeUsers() {
      try {
        const response = await axios.get('https://meeting-backend.iotech.my.id/ext-users', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const filteredUsers = response.data
          .filter(user => user.departmentName === 'Admin Project' || user.departmentName === 'Purchasing')
          .sort((a, b) => a.name.localeCompare(b.name));

        this.gspeNameOptions = filteredUsers.map(user => ({
          id: user.id,
          username: user.name,
          authorizationLevelName: user.authorizationLevelName,
        }));
      } catch (error) {
        console.error('Error fetching GSPE users:', error);
        this.gspeNameOptions = [];
      }
    },

    // --- PENAMBAHAN BARU: Method untuk memuat Job Options ---
    async loadJobOptions() {
      try {
        const response = await axios.get(`${window.location.origin}/api/jobfiles`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        this.jobOptions = response.data;
      } catch (error) {
        console.error('Error fetching job options:', error);
        this.jobOptions = [];
      }
    },

    async loadSpkDetailById(spkId) {
      if (!spkId) return;
      this.isLoading = true;
      try {
        const response = await axios.get(`${window.location.origin}/api/spksubconfiles/${spkId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const spk = response.data;

        // Populate main form data
        this.formData.spkNum = spk.Id;
        this.formData.subcon = spk.EntityId;
        await this.loadContactOptions(spk.EntityId);
        await this.$nextTick();
        this.formData.subconContactName = String(spk.ContactId);
        this.updateSubconInfo();
        this.formData.pekerjaan = spk.SpkJob; // DIUBAH: tidak perlu String() karena v-model akan handle
        this.formData.spkDate = spk.SpkDate ? spk.SpkDate.split('T')[0] : '';
        this.formData.remark = spk.SpkRemark;
        this.formData.gspeNama = String(spk.UserId);
        this.formData.poNumber = '';
        this.selectedPoObject = null;
        this.formData.siteName = '';

        const detailPromises = spk.TblSpksubconDetails.map(async (detail) => {
          let areaName = '';
          let projectName = '';
          const poObject = this.poOptions.find(p => p.id === detail.PoId);
          const poNumberString = poObject ? poObject.poNumber : '';
          if (detail.PoId) {
            try {
              const projectResponse = await axios.get(`https://meeting-backend.iotech.my.id/projects-bypo/${detail.PoId}`);
              if (projectResponse.data && projectResponse.data.length > 0) {
                areaName = projectResponse.data[0].city?.name || '';
                projectName = projectResponse.data[0].name || '';
              }
            } catch (err) {
              console.error(`Failed to fetch project details for PO ID ${detail.PoId}`, err);
            }
          }

          return {
            Id: detail.Id ? String(detail.Id) : '0',
            sitename: detail.Sitename,
            area: areaName,
            projectName: projectName,
            price: detail.Price,
            priceFormatted: new Intl.NumberFormat('id-ID').format(detail.Price),
            startDate: detail.StartDate ? detail.StartDate.split('T')[0] : '',
            endDate: detail.EndDate ? detail.EndDate.split('T')[0] : '',
            poId: detail.PoId,
            poNumber: poNumberString,
          };
        });

        this.detailPekerjaan = await Promise.all(detailPromises);

      } catch (error) {
        console.error('Gagal memuat detail SPK:', error);
        Swal.fire('Gagal', 'Gagal memuat data SPK yang dipilih.', 'error');
      } finally {
        this.isLoading = false; // PENAMBAHAN BARU
      }
    },

    async fetchProjectDetails(poId) {
      this.selectedProjectData = null;
      if (poId) {
        try {
          const response = await axios.get(`https://meeting-backend.iotech.my.id/projects-bypo/${poId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          this.selectedProjectData = response.data.length > 0 ? response.data[0] : null;
        } catch (error) {
          console.error(`Error fetching project details for PO ID ${poId}:`, error);
          this.selectedProjectData = null;
        }
      }
    },

    poOptionLabel(option) {
      return `${option.poNumber} (${option.siteNames})`;
    },
    getCurrentDate() {
      const today = new Date();
      return today.toISOString().split('T')[0];
    },

    updateSubconInfo() {
      const selectedContact = this.contactOptions.find(contact => contact.id == this.formData.subconContactName);
      if (selectedContact) {
        this.subconInfo.jabatan = selectedContact.contactjt;
        this.subconInfo.noHp = selectedContact.contactphone;
      } else {
        this.subconInfo.jabatan = '';
        this.subconInfo.noHp = '';
      }
    },

    filterSitesByPo(poId) {
      this.siteOptions = [];
      if (poId) {
        const selectedPo = this.poOptions.find(po => po.id == poId);
        this.siteOptions = selectedPo ? selectedPo.sites : [];
        this.fetchProjectDetails(poId);
      }
    },

    addRowToDetailPekerjaan() {
      if (!this.formData.poNumber) {
        Swal.fire('Perhatian', 'Harap pilih PO # terlebih dahulu.', 'warning');
        return;
      }

      const selectedSiteObject = this.siteOptions.find(site => site.id == this.formData.siteName);
      const siteNameToAdd = selectedSiteObject?.name || '';
      const siteIdToAdd = selectedSiteObject?.id || '';

      const hasUnfilledEmptyRow = this.detailPekerjaan.some(row => !row.siteId && (!row.sitename || row.sitename.trim() === ''));
      if (!siteNameToAdd) {
        if (hasUnfilledEmptyRow) {
          Swal.fire('Perhatian', 'Harap isi nama site pada baris kosong yang sudah ada.', 'warning');
          return;
        }
      }

      const isDuplicate = this.detailPekerjaan.some(row => row.sitename === siteNameToAdd && row.siteId === siteIdToAdd);
      if (isDuplicate) {
        Swal.fire('Perhatian', `Site "${siteNameToAdd}" sudah ada di detail pekerjaan.`, 'warning');
        return;
      }

      const newRow = {
        checked: false,
        siteId: siteIdToAdd || '0',
        sitename: siteNameToAdd,
        area: this.selectedProjectData?.city?.name || '',
        projectName: this.selectedProjectData?.name || '',
        price: 0,
        priceFormatted: '0',
        startDate: this.getCurrentDate(),
        endDate: this.getCurrentDate(),
        poId: this.formData.poNumber,
        poNumber: this.selectedPoObject ? this.selectedPoObject.poNumber : '',
      };
      this.detailPekerjaan.push(newRow);
      this.formData.siteName = '';
    },

    updatePrice(detail) {
      let cleanedValue = detail.priceFormatted.replace(/\D/g, '');
      detail.price = Number(cleanedValue);
      detail.priceFormatted = cleanedValue;
    },

    formatPriceOnBlur(detail) {
      if (detail.price !== null && !isNaN(detail.price)) {
        detail.priceFormatted = new Intl.NumberFormat('id-ID').format(detail.price);
      } else {
        detail.priceFormatted = '';
      }
    },

    // --- PENAMBAHAN BARU: Methods untuk Modal Job ---
    openJobModal() {
      this.newJobName = '';
      this.isJobModalVisible = true;
    },
    closeJobModal() {
      this.isJobModalVisible = false;
    },
    async handleSaveNewJob() {
      if (!this.newJobName.trim()) {
        Swal.fire('Input Kosong', 'Nama job tidak boleh kosong.', 'warning');
        return;
      }
      this.isSavingJob = true;
      try {
        const response = await axios.post(`${window.location.origin}/api/jobfiles`,
          { jobname: this.newJobName },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );

        await this.loadJobOptions(); // Muat ulang daftar job
        this.formData.pekerjaan = response.data.id; // Langsung pilih job baru
        Swal.fire('Berhasil', `Job '${response.data.jobname}' berhasil ditambahkan.`, 'success');
        this.closeJobModal();

      } catch (error) {
        let errorMessage = 'Gagal menyimpan job baru.';
        if (error.response?.status === 409) {
          errorMessage = error.response.data;
        }
        Swal.fire('Error', errorMessage, 'error');
        console.error("Error saving new job:", error);
      } finally {
        this.isSavingJob = false;
      }
    },
    async saveForm() {
      const id = this.formData.spkNum;
      if (!id) {
        Swal.fire('Invalid', 'SPK # tidak valid.', 'warning');
        return;
      }

      const token = localStorage.getItem('token');
      const detailsPayload = this.detailPekerjaan.map(detail => ({
        Id: detail.Id && detail.Id !== '0' ? parseInt(detail.Id) : null,
        SiteId: detail.siteId || null,
        Sitename: detail.sitename,
        Area: detail.area || '',
        ProjectName: detail.projectName || '',
        Price: detail.price || 0,
        StartDate: detail.startDate,
        EndDate: detail.endDate,
        Status: detail.status ?? (detail.checked ? 1 : 0),
        PoId: detail.poId ? parseInt(detail.poId) : null,
        PoNum: detail.poNumber || '',
        BalanceInv: detail.price || 0,
      }));

      const payload = {
        SpkDate: this.formData.spkDate,
        EntityId: parseInt(this.formData.subcon),
        ContactId: parseInt(this.formData.subconContactName),
        SpkJob: parseInt(this.formData.pekerjaan),
        SpkRemark: this.formData.remark,
        UserId: parseInt(this.formData.gspeNama),
        Status: 0,
        TblSpksubconDetails: detailsPayload
      };

      Swal.fire({
        title: 'Menyimpan data...',
        text: 'Mohon tunggu sebentar',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      try {
        await axios.put(`${window.location.origin}/api/spksubconfiles/${id}`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        Swal.fire('Berhasil', 'Data SPK berhasil diupdate!', 'success');
        this.resetForm();
      } catch (error) {
        console.error('Gagal menyimpan data SPK:', error);
        Swal.fire('Gagal', 'Terjadi kesalahan saat mengupdate data SPK.', 'error');
      }
    },

    resetForm() {
      this.formData = {
        spkNum: '',
        subcon: '',
        subconContactName: '',
        pekerjaan: '',
        spkDate: this.getCurrentDate(),
        remark: '',
        poNumber: '',
        siteName: '',
        gspeNama: '',
        gspeJabatan: '',
      };
      this.subconInfo = { jabatan: '', noHp: '' };
      this.detailPekerjaan = [];
      this.contactOptions = [];
      this.siteOptions = [];
      this.selectedProjectData = null;
      this.selectedPoObject = null;
    },

    exitForm() {
      this.$router.go(-1);
    },
  },

  // =================================================================
  // SECTION: LIFECYCLE & WATCHERS
  // =================================================================
  mounted() {
    this.loadSubconOptions();
    this.loadPoOptions();
    this.loadGspeUsers();
    this.loadSpkListOptions();
    this.loadJobOptions(); // PENAMBAHAN BARU
  },

  watch: {
    'formData.spkNum'(newId) {
      if (newId) {
        this.loadSpkDetailById(newId);
      } else {
        this.resetForm();
      }
    },
    'formData.subcon'(newSubconId) {
      this.loadContactOptions(newSubconId);
      this.formData.subconContactName = '';
      this.subconInfo = { jabatan: '', noHp: '' };
    },
    selectedPoObject(newVal) {
      this.formData.poNumber = newVal ? newVal.id : '';
      this.formData.siteName = '';
      this.filterSitesByPo(this.formData.poNumber);
    },
    'formData.gspeNama'(newGspeUserId) {
      const selectedUser = this.gspeNameOptions.find(user => user.id == newGspeUserId);
      this.formData.gspeJabatan = selectedUser ? selectedUser.authorizationLevelName : '';
    },
  },
};
</script>

<style scoped>
.input-border {
  border-bottom: 2px solid #ccc;
  border-radius: 0;
  border-left: none;
  border-right: none;
  border-top: none;
}

.input-border:focus {
  border-bottom-color: #007bff;
  box-shadow: none;
}

.v-select.input-border .vs__dropdown-toggle {
  border: 1px solid #d2d6dc;
  border-radius: 0.25rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  padding-left: 0.75rem;
  border-bottom: 1px solid #d2d6dc;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  background-color: #fff;
}

.v-select.input-border .vs__selected-options {
  padding: 0;
}

.v-select.input-border .vs__selected {
  margin: 0;
  padding: 0;
  line-height: 1.25rem;
  color: #4a5568;
}

.v-select.input-border .vs__search::placeholder,
.v-select.input-border .vs__search {
  color: #a0aec0;
}

.v-select.input-border.vs--open .vs__dropdown-toggle,
.v-select.input-border.vs--single.vs--open .vs__dropdown-toggle {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.5);
}


.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 5px;
  font-weight: bold;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

.form-group textarea {
  resize: vertical;
}

.form-actions {
  margin-top: 20px;
}

.form-actions button {
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.form-actions button:first-child {
  background-color: #28a745;
  color: white;
  margin-right: 10px;
}

.form-actions button:last-child {
  background-color: #dc3545;
  color: white;
}

.data-grid-container {
  margin-top: 20px;
  border: 1px solid #ddd;
  padding: 15px;
  border-radius: 4px;
}

.data-grid-container h3 {
  margin-top: 0;
  margin-bottom: 10px;
}

.data-grid-container table {
  width: 100%;
  border-collapse: collapse;
}

.data-grid-container th,
.data-grid-container td {
  border: 1px solid #eee;
  padding: 8px;
  text-align: left;
}

.data-grid-container th {
  background-color: #f0f0f0;
  font-weight: bold;
}

.gspe-info {
  margin-top: 20px;
  border: 1px solid #ddd;
  padding: 15px;
  border-radius: 4px;
}

.gspe-info h3 {
  margin-top: 0;
  margin-bottom: 10px;
}

.gspe-info .form-actions {
  margin-top: 15px;
  display: flex;
  justify-content: flex-start;
  gap: 10px;
}
</style>